<template>
	<div>
		<!-- Auto Campaigns Section -->
		<div class="campaigns-section mb-4">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link to="/auto-campaigns" class="back-btn me-3" title="Back to Auto Campaigns">
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">Auto Campaigns</h2>
							<p class="section-subtitle">Automated campaigns that trigger based on events or schedules</p>
						</div>
					</div>
					<router-link :to="{ name: 'createAutoCampaign' }" class="add-campaign-btn">
						<i class="fad fa-plus btn-icon"></i>
						<span class="btn-tooltip">Create Campaign</span>
					</router-link>
				</div>
			</div>

			<!-- Secondary Tabs -->
			<div class="secondary-tabs-container">
				<div class="secondary-tabs">
					<!-- Campaign Type Filters -->
					<button class="secondary-tab campaign-type-tab" :class="{ active: campaignTypeFilter === 'triggers' }" @click="setCampaignTypeFilter('triggers')">
						<i class="fad fa-bolt me-2"></i>
						<span class="secondary-tab-text">With Triggers</span>
						<span class="secondary-tab-badge">{{ getTriggersCount() }}</span>
					</button>
					<button class="secondary-tab campaign-type-tab" :class="{ active: campaignTypeFilter === 'sequences' }" @click="setCampaignTypeFilter('sequences')">
						<i class="fad fa-list-ol me-2"></i>
						<span class="secondary-tab-text">With Sequences</span>
						<span class="secondary-tab-badge">{{ getSequencesCount() }}</span>
					</button>
					
					<!-- Divider -->
					<div class="tabs-divider"></div>
					
					<!-- Status Filters -->
					<button class="secondary-tab" :class="{ active: secondaryTab === 'all' }" @click="setSecondaryTab('all')">
						<span class="secondary-tab-text">All</span>
						<span class="secondary-tab-badge">{{ getAllCount() }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: secondaryTab === 'active' }" @click="setSecondaryTab('active')">
						<span class="secondary-tab-text">Active</span>
						<span class="secondary-tab-badge">{{ getActiveCount() }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: secondaryTab === 'paused' }" @click="setSecondaryTab('paused')">
						<span class="secondary-tab-text">Paused</span>
						<span class="secondary-tab-badge">{{ getPausedCount() }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: secondaryTab === 'draft' }" @click="setSecondaryTab('draft')">
						<span class="secondary-tab-text">Draft</span>
						<span class="secondary-tab-badge">{{ getDraftCount() }}</span>
					</button>
					<button v-if="campaignTypeFilter === 'sequences'" class="secondary-tab" :class="{ active: secondaryTab === 'finished' }" @click="setSecondaryTab('finished')">
						<span class="secondary-tab-text">Finished</span>
						<span class="secondary-tab-badge">{{ getFinishedCount() }}</span>
					</button>
				</div>

				<!-- Search -->
				<div class="search-input-wrapper">
					<i class="fad fa-search search-icon"></i>
					<input type="text" class="search-input" placeholder="Search campaigns" v-model="searchTerm" @input="filterCampaigns" />
				</div>
			</div>

			<!-- Campaigns Table -->
			<div class="campaigns-table">
				<!-- Table Header - Triggers -->
				<div v-if="campaignTypeFilter === 'triggers'" class="table-header">
					<div class="header-cell campaign-col">Campaign</div>
					<div class="header-cell preview-col"></div>
					<div class="header-cell trigger-col">Trigger</div>
					<div class="header-cell type-col">Type</div>
					<div class="header-cell status-col">Status</div>
					<div class="header-cell runs-col">Total Runs</div>
					<div class="header-cell date-col">Last Run</div>
					<div class="header-cell actions-col"></div>
				</div>

				<!-- Table Header - Sequences -->
				<div v-if="campaignTypeFilter === 'sequences'" class="table-header sequences-table-header">
					<div class="header-cell campaign-col">Campaign</div>
					<div class="header-cell preview-col"></div>
					<div class="header-cell sequences-col">Sequences</div>
					<div class="header-cell status-col">Status</div>
					<div class="header-cell author-col">Author</div>
					<div class="header-cell actions-col"></div>
				</div>

				<!-- Empty State -->
				<div v-if="filteredCampaigns.length === 0" class="empty-state">
					<div class="empty-state-content">
						<div class="empty-state-icon">
							<i v-if="campaignTypeFilter === 'triggers'" class="fad fa-bolt"></i>
							<i v-else-if="campaignTypeFilter === 'sequences'" class="fad fa-list-ol"></i>
							<i v-else class="fad fa-envelope-open-text"></i>
						</div>
						<h3 class="empty-state-title">{{ getEmptyStateTitle() }}</h3>
						<p class="empty-state-description">
							{{ getEmptyStateDescription() }}
						</p>
						<div class="empty-state-actions" v-if="!hasStatusFilter()">
							<router-link :to="getCreateCampaignRoute()" class="empty-state-btn">
								<i class="fad fa-plus me-2"></i>
								{{ getEmptyStateButtonText() }}
							</router-link>
						</div>
						<div class="empty-state-help" v-if="!hasStatusFilter()">
							<div class="help-item" v-if="campaignTypeFilter === 'triggers'">
								<i class="fad fa-info-circle me-2"></i>
								Trigger campaigns send automatically based on events
							</div>
							<div class="help-item" v-if="campaignTypeFilter === 'triggers'">
								<i class="fad fa-cog me-2"></i>
								Set up triggers, templates, and targeting rules
							</div>
							<div class="help-item" v-if="campaignTypeFilter === 'sequences'">
								<i class="fad fa-info-circle me-2"></i>
								Sequence campaigns send timed email series
							</div>
							<div class="help-item" v-if="campaignTypeFilter === 'sequences'">
								<i class="fad fa-cog me-2"></i>
								Create email sequences with delays and automation
							</div>
						</div>
						<div class="empty-state-help" v-else>
							<div class="help-item">
								<i class="fad fa-info-circle me-2"></i>
								Try adjusting your filters to see more campaigns
							</div>
						</div>
					</div>
				</div>

				<!-- Table Rows - Triggers -->
				<div v-else-if="campaignTypeFilter === 'triggers'" class="table-row" v-for="campaign in filteredCampaigns" :key="campaign.id">
					<div class="table-cell campaign-cell">
						<div class="campaign-info">
							<div class="campaign-avatar">
								{{ getInitials(campaign.name) }}
							</div>
							<div class="campaign-details">
								<div class="campaign-name">
									{{ campaign.name }}
									<span v-if="campaign.mode === 'test'" class="test-label">TEST</span>
								</div>
								<div class="campaign-description">{{ campaign.description }}</div>
							</div>
						</div>
					</div>
					<div class="table-cell preview-cell">
						<div class="preview-content">
							<!-- Postcard Preview -->
							<template v-if="campaign.type === 'postcard' && campaign.postcard_front">
								<a :href="getPostcardImageUrl(campaign.postcard_front)" target="_blank" class="preview-link" title="View postcard front">
									<img :src="getPostcardImageUrl(campaign.postcard_front)" alt="Postcard preview" class="postcard-preview" />
								</a>
							</template>

							<!-- Letter Preview -->
							<template v-else-if="campaign.type === 'letter' && campaign.letter_pdf">
								<a :href="getLetterPdfUrl(campaign.letter_pdf)" target="_blank" class="preview-link" title="View letter PDF">
									<i class="fad fa-file-pdf fa-2x letter-icon"></i>
								</a>
							</template>

							<!-- No Preview Available -->
							<template v-else>
								<div class="no-preview" title="No preview available">
									<i class="fad fa-file-question fa-lg no-preview-icon"></i>
								</div>
							</template>
						</div>
					</div>
					<div class="table-cell trigger-cell">
						<div class="trigger-info">
							<template v-if="campaign.trigger && typeof campaign.trigger === 'object' && campaign.trigger.name">
								<div class="trigger-type" :title="campaign.trigger.reason">{{ campaign.trigger.name }}</div>
							</template>
							<template v-else>
								<div class="trigger-type">{{ campaign.trigger_id }}</div>
							</template>
						</div>
					</div>
					<div class="table-cell type-cell">
						<span class="type-badge" :class="campaign.type">
							{{ campaign.type === 'letter' ? 'Letter' : 'Postcard' }}
						</span>
					</div>
					<div class="table-cell status-cell">
						<span class="status-badge" :class="campaign.status">
							<i v-if="campaign.status === 'finished'" class="fad fa-check"></i>
							<i v-else-if="campaign.status === 'active'" class="fad fa-truck"></i>
							{{ campaign.status }}
						</span>
					</div>
					<div class="table-cell runs-cell">{{ campaign.runs.length }}</div>

					<div class="table-cell date-cell">{{ formatLastRunDate(campaign.last_run) }}</div>
					<div class="table-cell actions-cell">
						<button type="button" class="action-icon-btn" @click="viewCampaignRuns(campaign)" title="View Run Instances">
							<i class="fad fa-sitemap"></i>
						</button>
						<button type="button" class="action-icon-btn" @click="editCampaign(campaign)" title="Edit">
							<i class="fad fa-pen"></i>
						</button>
						<button type="button" class="action-icon-btn delete-btn" @click="deleteCampaign(campaign)" title="Delete">
							<i class="fad fa-trash"></i>
						</button>
					</div>
				</div>

				<!-- Table Rows - Sequences -->
				<div v-else-if="campaignTypeFilter === 'sequences'" class="table-row sequences-table-row" v-for="campaign in filteredCampaigns" :key="campaign.id">
					<div class="table-cell campaign-cell">
						<div class="campaign-info">
							<div class="campaign-avatar">
								<i class="fad fa-envelope-open-text"></i>
							</div>
							<div class="campaign-details">
								<div class="campaign-name">
									{{ campaign.name }}
									<span v-if="campaign.mode === 'test'" class="test-label">TEST</span>
								</div>
								<div class="campaign-description">{{ campaign.description }}</div>
							</div>
						</div>
					</div>
					<div class="table-cell preview-cell">
						<div class="preview-content">
							<!-- Removed envelope icon from here -->
						</div>
					</div>
					<div class="table-cell sequences-cell">
						<div class="sequences-info">
							<div v-if="campaign.sequences && campaign.sequences.length > 0" class="sequences-detailed">
								<div class="sequences-list">
									<div v-for="sequence in campaign.sequences" :key="sequence.id" class="sequence-item">
										<div class="sequence-content">
											<span class="sequence-position">#{{ (sequence.position || 0) + 1 }}</span>
											<span class="sequence-type-badge" :class="sequence.type">{{ sequence.type }}</span>
											<span class="sequence-status" :class="getSequenceStatusClass(sequence)">
												<i v-if="getSequenceStatus(sequence) === 'completed'" class="fad fa-check"></i>
												<i v-else-if="getSequenceStatus(sequence) === 'pending'" class="fad fa-circle"></i>
												<i v-else-if="getSequenceStatus(sequence) === 'sending'" class="fad fa-paper-plane"></i>
												{{ getSequenceStatus(sequence) }}
											</span>
											<span v-if="sequence.sequence_start_at" class="sequence-start-date">{{ formatSequenceDate(sequence.sequence_start_at) }}</span>
											<span v-if="shouldShowSequenceCost(sequence)" class="sequence-cost">${{ formatCost(sequence.cost || 0) }}</span>
										</div>
									</div>
								</div>
							</div>
							<div v-else class="no-sequences">
								<span class="empty-sequences">No sequences configured</span>
							</div>
						</div>
					</div>
					<div class="table-cell status-cell">
						<span class="status-badge" :class="campaign.status">
							<i v-if="campaign.status === 'finished'" class="fad fa-check"></i>
							<i v-else-if="campaign.status === 'active'" class="fad fa-truck"></i>
							{{ campaign.status }}
						</span>
					</div>
					<div class="table-cell author-cell">
						<div class="author-info">
							{{ getAuthorName(campaign) }}
						</div>
					</div>
					<div class="table-cell actions-cell">
						<button type="button" class="action-icon-btn" @click="viewSequenceAnalytics(campaign)" title="View Analytics">
							<i class="fad fa-chart-line"></i>
						</button>
						<button type="button" class="action-icon-btn" @click="editCampaign(campaign)" title="Edit">
							<i class="fad fa-pen"></i>
						</button>
						<button type="button" class="action-icon-btn delete-btn" @click="deleteCampaign(campaign)" title="Delete">
							<i class="fad fa-trash"></i>
						</button>
					</div>
				</div>
			</div>
		</div>


		<!-- Bootstrap Tab Navigation -->
		<ul class="nav nav-pills custom-nav-pills mb-4 d-none" id="mainTabs" role="tablist">
			<li class="nav-item" role="presentation">
				<button
					class="nav-link active"
					id="members-tab"
					data-bs-toggle="tab"
					data-bs-target="#members"
					type="button"
					role="tab"
					aria-controls="members"
					aria-selected="true"
				>
					<span class="tab-text">Members</span>
					<span class="tab-badge">{{ getTotalCount() }}</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="meetings-tab" data-bs-toggle="tab" data-bs-target="#meetings" type="button" role="tab" aria-controls="meetings" aria-selected="false">
					<span class="tab-text">Meetings</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="events-tab" data-bs-toggle="tab" data-bs-target="#events" type="button" role="tab" aria-controls="events" aria-selected="false">
					<span class="tab-text">Events</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="forms-tab" data-bs-toggle="tab" data-bs-target="#forms" type="button" role="tab" aria-controls="forms" aria-selected="false">
					<span class="tab-text">Forms</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="payments-tab" data-bs-toggle="tab" data-bs-target="#payments" type="button" role="tab" aria-controls="payments" aria-selected="false">
					<span class="tab-text">Payments</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="venues-tab" data-bs-toggle="tab" data-bs-target="#venues" type="button" role="tab" aria-controls="venues" aria-selected="false">
					<span class="tab-text">Venues</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="news-tab" data-bs-toggle="tab" data-bs-target="#news" type="button" role="tab" aria-controls="news" aria-selected="false">
					<span class="tab-text">News & Notices</span>
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button class="nav-link" id="faqs-tab" data-bs-toggle="tab" data-bs-target="#faqs" type="button" role="tab" aria-controls="faqs" aria-selected="false">
					<span class="tab-text">FAQs</span>
				</button>
			</li>
		</ul>

		<!-- Tab Content -->
		<div class="tab-content d-none" id="mainTabsContent">
			<!-- Members Tab -->
			<div class="tab-pane fade show active" id="members" role="tabpanel" aria-labelledby="members-tab">
				<div class="text-center p-5">
					<h3>Members</h3>
					<p class="text-muted">Members content is shown above in the main section.</p>
				</div>
			</div>

			<!-- Meetings Tab -->
			<div class="tab-pane fade" id="meetings" role="tabpanel" aria-labelledby="meetings-tab">
				<div class="text-center p-5">
					<h3>Meetings</h3>
					<p class="text-muted">Meetings content coming soon...</p>
				</div>
			</div>

			<!-- Events Tab -->
			<div class="tab-pane fade" id="events" role="tabpanel" aria-labelledby="events-tab">
				<div class="text-center p-5">
					<h3>Events</h3>
					<p class="text-muted">Events content coming soon...</p>
				</div>
			</div>

			<!-- Forms Tab -->
			<div class="tab-pane fade" id="forms" role="tabpanel" aria-labelledby="forms-tab">
				<div class="text-center p-5">
					<h3>Forms</h3>
					<p class="text-muted">Forms content coming soon...</p>
				</div>
			</div>

			<!-- Payments Tab -->
			<div class="tab-pane fade" id="payments" role="tabpanel" aria-labelledby="payments-tab">
				<div class="text-center p-5">
					<h3>Payments</h3>
					<p class="text-muted">Payments content coming soon...</p>
				</div>
			</div>

			<!-- Venues Tab -->
			<div class="tab-pane fade" id="venues" role="tabpanel" aria-labelledby="venues-tab">
				<div class="text-center p-5">
					<h3>Venues</h3>
					<p class="text-muted">Venues content coming soon...</p>
				</div>
			</div>

			<!-- News & Notices Tab -->
			<div class="tab-pane fade" id="news" role="tabpanel" aria-labelledby="news-tab">
				<div class="text-center p-5">
					<h3>News & Notices</h3>
					<p class="text-muted">News & Notices content coming soon...</p>
				</div>
			</div>

			<!-- FAQs Tab -->
			<div class="tab-pane fade" id="faqs" role="tabpanel" aria-labelledby="faqs-tab">
				<div class="text-center p-5">
					<h3>FAQs</h3>
					<p class="text-muted">FAQs content coming soon...</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index'

export default {
	name: 'AutoCampaigns',
	data() {
		return {
			triggerCampaigns: [],
			sequenceCampaigns: [],
			campaigns: [],
			filteredCampaigns: [],
			searchTerm: '',
			activeTab: 'all',
			secondaryTab: 'all',
			campaignTypeFilter: localStorage.getItem('autoCampaignTypeFilter') || 'triggers', // Persist tab selection
		}
	},
	computed: {
	},
	async mounted() {
		await this.loadCampaigns()
	},
	methods: {
		async loadCampaigns() {
			try {
				// Load trigger campaigns from the existing endpoint
				const triggerResponse = await restApi.get('/auto-campaigns')
				console.log('trigger response', triggerResponse)
				this.triggerCampaigns = triggerResponse.data.map(campaign => ({
					...campaign,
					campaign_type: 'trigger'
				}))

				// Load sequence campaigns from the new endpoint
				const sequenceResponse = await restApi.get('/sequenced-campaigns')
				console.log('sequence response', sequenceResponse)
				this.sequenceCampaigns = sequenceResponse.data.map(campaign => ({
					...campaign,
					campaign_type: 'sequence',
					has_sequences: true
				}))

				// Combine both types of campaigns
				this.campaigns = [...this.triggerCampaigns, ...this.sequenceCampaigns]
				this.filterCampaigns()
			} catch (error) {
				console.error('Error loading campaigns:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to load campaigns',
					icon: 'error',
				})
			}
		},
		filterCampaigns() {
			this.filteredCampaigns = this.campaigns.filter(campaign => {
				const matchesSearch =
					campaign.name.toLowerCase().includes(this.searchTerm.toLowerCase()) || campaign.description.toLowerCase().includes(this.searchTerm.toLowerCase())
				const matchesSecondaryTab = this.secondaryTab === 'all' || campaign.status === this.secondaryTab
				
				// Filter by campaign type (triggers vs sequences)
				const matchesCampaignType = this.campaignTypeFilter === 'triggers' ? campaign.campaign_type === 'trigger' : campaign.campaign_type === 'sequence'

				return matchesSearch && matchesSecondaryTab && matchesCampaignType
			})
		},
		setActiveTab(tab) {
			this.activeTab = tab
			this.filterCampaigns()
		},
		setSecondaryTab(tab) {
			this.secondaryTab = tab
			this.filterCampaigns()
		},
		getTotalCount() {
			return this.campaigns.length
		},
		getAllCount() {
			return this.getFilteredCampaignsByType().length
		},
		getActiveCount() {
			return this.getFilteredCampaignsByType().filter(campaign => campaign.status === 'active').length
		},
		getPausedCount() {
			return this.getFilteredCampaignsByType().filter(campaign => campaign.status === 'paused').length
		},
		getDraftCount() {
			return this.getFilteredCampaignsByType().filter(campaign => campaign.status === 'draft').length
		},
		getFinishedCount() {
			return this.getFilteredCampaignsByType().filter(campaign => campaign.status === 'finished').length
		},
		getFilteredCampaignsByType() {
			return this.campaigns.filter(campaign => {
				return this.campaignTypeFilter === 'triggers' ? campaign.campaign_type === 'trigger' : campaign.campaign_type === 'sequence'
			})
		},
		getTriggersCount() {
			return this.campaigns.filter(campaign => campaign.campaign_type === 'trigger').length
		},
		getSequencesCount() {
			return this.campaigns.filter(campaign => campaign.campaign_type === 'sequence').length
		},
		setCampaignTypeFilter(type) {
			// If switching away from sequences and user was on finished tab, reset to all
			if (this.campaignTypeFilter === 'sequences' && type === 'triggers' && this.secondaryTab === 'finished') {
				this.secondaryTab = 'all'
			}
			this.campaignTypeFilter = type
			localStorage.setItem('autoCampaignTypeFilter', type)
			this.filterCampaigns()
		},
		getInitials(name) {
			return name
				.split(' ')
				.map(word => word.charAt(0))
				.join('')
				.substring(0, 2)
				.toUpperCase()
		},
		getStatusBadgeClass(status) {
			const classes = {
				active: 'bg-success',
				paused: 'bg-warning',
				completed: 'bg-secondary',
			}
			return classes[status] || 'bg-secondary'
		},
		editCampaign(campaign) {
			if (campaign.campaign_type === 'sequence') {
				this.$router.push(`/auto-campaigns/edit/${campaign.id}/sequences`)
			} else {
				this.$router.push({
					name: 'editAutoCampaign',
					params: { id: campaign.id },
				})
			}
		},
		viewCampaignRuns(campaign) {
			// Navigate to campaign runs view
			this.$router.push({
				name: 'autoCampaignRuns',
				params: { id: campaign.id },
			})
		},
		async toggleCampaignStatus(campaign) {
			try {
				// Mock API call - replace with actual implementation
				campaign.status = campaign.status === 'active' ? 'paused' : 'active'

				this.$swal.fire({
					title: 'Success',
					text: `Campaign ${campaign.status === 'active' ? 'activated' : 'paused'}`,
					icon: 'success',
					timer: 2000,
					showConfirmButton: false,
				})
			} catch (error) {
				console.error('Error toggling campaign status:', error)
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to update campaign status',
					icon: 'error',
				})
			}
		},
		async deleteCampaign(campaign) {
			// Format campaign information for display
			const campaignType = campaign.type === 'letter' ? 'Letter' : 'Postcard'
			const campaignMode = campaign.mode === 'test' ? '🧪 Test Mode' : '🚀 Live Mode'
			const campaignStatus = campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)
			const totalRuns = campaign.total_runs || 0
			const totalSent = campaign.total_sent || 0
			const lastRun = campaign.last_run ? this.formatLastRunDate(campaign.last_run) : 'Never'

			// Get trigger name if available
			const triggerName = campaign.trigger && typeof campaign.trigger === 'object' && campaign.trigger.name ? campaign.trigger.name : campaign.trigger_id || 'Not set'

			const result = await this.$swal.fire({
				title: 'Delete Auto Campaign?',
				html: `
					<div style="text-align: left; margin: 15px 0;">
						<div style="background: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 16px;">
							<h4 style="margin: 0 0 12px 0; color: #1a202c; font-size: 16px; font-weight: 600;">${campaign.name}</h4>
							<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 14px;">
								<div><strong>Type:</strong> ${campaignType}</div>
								<div><strong>Mode:</strong> ${campaignMode}</div>
								<div><strong>Status:</strong> ${campaignStatus}</div>
								<div><strong>Trigger:</strong> ${triggerName}</div>
								<div><strong>Total Runs:</strong> ${totalRuns}</div>
								<div><strong>Total Sent:</strong> ${totalSent}</div>
								<div style="grid-column: 1 / -1;"><strong>Last Run:</strong> ${lastRun}</div>
								${
									campaign.description
										? `<div style="grid-column: 1 / -1; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0;"><strong>Description:</strong><br><em style="color: #718096;">${campaign.description}</em></div>`
										: ''
								}
							</div>
						</div>
						<div style="text-align: center; color: #e53e3e; font-weight: 500; font-size: 14px;">
							⚠️ This action cannot be undone!
						</div>
					</div>
				`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#3085d6',
				confirmButtonText: 'Yes, delete it!',
				cancelButtonText: 'Cancel',
				width: '500px',
				// Override global toast settings for this dialog
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})

			if (result.isConfirmed) {
				try {
					// Delete campaign via appropriate API endpoint
					if (campaign.campaign_type === 'sequence') {
						await restApi.delete(`/sequenced-campaigns/${campaign.id}`)
					} else {
						await restApi.delete(`/auto-campaigns/${campaign.id}`)
					}
					
					this.campaigns = this.campaigns.filter(c => c.id !== campaign.id)
					this.filterCampaigns()

					this.$swal.fire({
						icon: 'success',
						title: 'Campaign deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})
				} catch (error) {
					console.error('Error deleting campaign:', error)

					let errorMessage = 'An error occurred while deleting the campaign'
					if (error.response?.data?.message) {
						errorMessage = error.response.data.message
					}

					this.$swal.fire({
						icon: 'error',
						title: 'Failed to delete campaign',
						text: errorMessage,
					})
				}
			}
		},
		formatDate(dateString) {
			if (!dateString) return 'Never'
			const date = new Date(dateString)
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
		},
		formatLastRunDate(dateString) {
			if (!dateString) return 'Never'
			const date = new Date(dateString)
			return date.toLocaleDateString()
		},
		formatLastActivity(dateString) {
			if (!dateString) return 'No activity'
			const date = new Date(dateString)
			return date.toLocaleDateString()
		},
		getAuthorName(campaign) {
			if (campaign.author && campaign.author.name) {
				return campaign.author.name
			}
			if (campaign.user && campaign.user.name) {
				return campaign.user.name
			}
			if (campaign.created_by) {
				return campaign.created_by
			}
			return 'Unknown'
		},
		getSequenceStatus(sequence) {
			// Return sequence status or fallback to 'pending' if not set
			return sequence.status || 'pending'
		},
		getSequenceStatusClass(sequence) {
			const status = this.getSequenceStatus(sequence)
			return `status-${status}`
		},
		shouldShowSequenceCost(sequence) {
			// Show cost for sequences that are sending or completed
			const status = this.getSequenceStatus(sequence)
			return status === 'sending' || status === 'completed' || status === 'active'
		},
		formatCost(cost) {
			if (!cost || cost === 0) return '0.00'
			return parseFloat(cost).toFixed(2)
		},
		formatSequenceDate(dateString) {
			if (!dateString) return ''
			const date = new Date(dateString)
			return date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			})
		},
		viewSequenceAnalytics(campaign) {
			// Placeholder method for viewing sequence analytics
			this.$swal.fire({
				title: 'Sequence Analytics',
				text: `Analytics for "${campaign.name}" will be available soon.`,
				icon: 'info'
			})
		},
		hasStatusFilter() {
			return this.secondaryTab !== 'all'
		},
		getEmptyStateTitle() {
			if (this.hasStatusFilter()) {
				const statusText = this.secondaryTab.charAt(0).toUpperCase() + this.secondaryTab.slice(1)
				if (this.campaignTypeFilter === 'triggers') {
					return `No ${statusText} Trigger Campaigns`
				} else if (this.campaignTypeFilter === 'sequences') {
					return `No ${statusText} Sequence Campaigns`
				}
				return `No ${statusText} Campaigns`
			}
			
			if (this.campaignTypeFilter === 'triggers') {
				return 'No Trigger Campaigns Yet'
			} else if (this.campaignTypeFilter === 'sequences') {
				return 'No Sequence Campaigns Yet'
			}
			return 'No Auto Campaigns Yet'
		},
		getEmptyStateDescription() {
			if (this.hasStatusFilter()) {
				const statusText = this.secondaryTab.toLowerCase()
				const campaignType = this.campaignTypeFilter === 'triggers' ? 'trigger' : 'sequence'
				return `You don't have any ${statusText} ${campaignType} campaigns at the moment. Try changing your filter or create a new campaign.`
			}
			
			if (this.campaignTypeFilter === 'triggers') {
				return 'Create your first trigger campaign to start sending targeted letters and postcards based on events and triggers.'
			} else if (this.campaignTypeFilter === 'sequences') {
				return 'Create your first sequence campaign to start sending automated email series to nurture leads and engage customers.'
			}
			return 'Create your first automated campaign to start sending targeted content.'
		},
		getEmptyStateButtonText() {
			if (this.campaignTypeFilter === 'triggers') {
				return 'Create Trigger Campaign'
			} else if (this.campaignTypeFilter === 'sequences') {
				return 'Create Sequence Campaign'
			}
			return 'Create Your First Campaign'
		},
		getCreateCampaignRoute() {
			if (this.campaignTypeFilter === 'triggers') {
				return { name: 'createAutoCampaignWithTriggers' }
			} else if (this.campaignTypeFilter === 'sequences') {
				return { name: 'createAutoCampaignWithSequences' }
			}
			return { name: 'createAutoCampaign' }
		},
		getPostcardImageUrl(imagePath) {
			if (!imagePath) return ''
			// If already a full URL, return as is
			if (imagePath.startsWith('http')) {
				return imagePath
			}
			// Construct full URL from storage path
			const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
			return `${baseUrl}/storage/${imagePath}`
		},
		getLetterPdfUrl(pdfPath) {
			if (!pdfPath) return ''
			// If already a full URL, return as is
			if (pdfPath.startsWith('http')) {
				return pdfPath
			}
			// Construct full URL from storage path
			const baseUrl = process.env.NODE_ENV === 'production' ? 'https://mc2api.townweb.com' : 'http://127.0.0.1:8000'
			return `${baseUrl}/storage/${pdfPath}`
		},
	},
}
</script>

<style scoped>
/* Bootstrap Tab Navigation Override */
.custom-nav-pills {
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
	margin-bottom: 24px;
	width: fit-content;
	gap: 0;
}

.custom-nav-pills .nav-item {
	margin: 0;
}

.custom-nav-pills .nav-link {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
	margin: 0;
}

.custom-nav-pills .nav-link.active {
	background-color: #ffffff !important;
	color: #111827 !important;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.custom-nav-pills .nav-link:hover:not(.active) {
	color: #374151;
	background-color: transparent;
}

.tab-text {
	font-weight: 500;
}

.tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.custom-nav-pills .nav-link.active .tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Campaigns Section */
.campaigns-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.section-header {
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.add-campaign-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.add-campaign-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.add-campaign-btn .btn-icon {
	transition: transform 0.3s ease;
}

.add-campaign-btn:hover .btn-icon {
	transform: rotate(180deg);
}

.btn-tooltip {
	position: absolute;
	right: 50px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.add-campaign-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Secondary Tabs Container */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 24px 24px 24px;
}

.secondary-tabs {
	display: flex;
	gap: 0;
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.secondary-tab {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;
}

.secondary-tab.active {
	background-color: #ffffff;
	color: #111827;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.secondary-tab:hover:not(.active) {
	color: #374151;
}

.secondary-tab-text {
	font-weight: 500;
}

.secondary-tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.secondary-tab.active .secondary-tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Campaigns Table */
.campaigns-table {
	border-top: 1px solid #f3f4f6;
}

.table-header {
	display: grid;
	grid-template-columns: 2fr 60px 1.5fr 100px auto 100px 100px 120px 80px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

/* Sequences table specific layout */
.sequences-table-header {
	grid-template-columns: 2fr 60px 2.5fr auto 150px 80px !important;
}

.sequences-table-row {
	grid-template-columns: 2fr 60px 2.5fr auto 150px 80px !important;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.table-row {
	display: grid;
	grid-template-columns: 2fr 60px 1.5fr 100px auto 100px 100px 120px 80px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Campaign Info */
.campaign-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.campaign-avatar {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	background-color: #e0f2fe;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	color: #0369a1;
	flex-shrink: 0;
}

.campaign-details {
	min-width: 0;
}

.campaign-name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
	display: flex;
	align-items: center;
	gap: 8px;
}

.test-label {
	background-color: #fed7aa;
	color: #ea580c;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.campaign-description {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}

/* Trigger Info */
.trigger-info {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.trigger-type {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

.trigger-details {
	font-size: 12px;
	color: #6b7280;
}

/* Type Badge */
.type-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.type-badge.letter {
	background-color: #dbeafe;
	color: #1e40af;
}

.type-badge.postcard {
	background-color: #e0f2fe;
	color: #0369a1;
}

/* Status Badge */
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
	display: flex;
	align-items: center;
	gap: 4px;
}

.status-badge i {
	font-size: 10px;
}

.status-badge.active {
	background-color: #dbeafe;
	color: #1e40af;
}

.status-badge.finished {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.paused {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.draft {
	background-color: #f3f4f6;
	color: #374151;
}

/* Table Cells */
.runs-cell {
	color: #6b7280;
	font-weight: 500;
	text-align: center;
}

.sent-cell {
	color: #6b7280;
	font-weight: 500;
	text-align: center;
}

.date-cell {
	color: #6b7280;
	font-size: 13px;
}

/* Actions */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	background: none;
	border: none;
	padding: 8px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
}

.action-icon-btn.delete-btn:hover {
	background-color: #fef2f2;
	color: #dc2626;
}

/* Pagination */
.pagination-container {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 24px;
	padding: 16px 0;
}

.pagination-info {
	font-size: 14px;
	color: #6b7280;
	margin-right: 20px;
}

.pagination-controls {
	display: flex;
	gap: 8px;
}

.pagination-btn {
	padding: 8px 16px;
	border: 1px solid #d1d5db;
	background: white;
	border-radius: 6px;
	font-size: 14px;
	color: #374151;
	cursor: pointer;
	transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
	background-color: #f9fafb;
	border-color: #9ca3af;
}

.pagination-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Preview Column Styles */
.preview-cell {
	display: flex;
	align-items: center;
	justify-content: center;
}

.preview-content {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 40px;
}

.preview-link {
	display: flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	transition: all 0.2s ease;
}

.preview-link:hover {
	transform: scale(1.1);
}

.postcard-preview {
	width: 40px;
	height: 30px;
	object-fit: cover;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	transition: all 0.2s ease;
}

.postcard-preview:hover {
	border-color: #3b82f6;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
}

.letter-icon {
	color: #dc2626;
	transition: all 0.2s ease;
}

.letter-icon:hover {
	color: #b91c1c;
	transform: scale(1.1);
}

.no-preview {
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0.5;
}

.no-preview-icon {
	color: #9ca3af;
}

/* Empty State */
.empty-state {
	padding: 60px 24px;
	text-align: center;
	border-bottom: 1px solid #f3f4f6;
}

.empty-state-content {
	max-width: 500px;
	margin: 0 auto;
}

.empty-state-icon {
	width: 80px;
	height: 80px;
	background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 24px;
	color: #9ca3af;
	font-size: 32px;
}

.empty-state-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 12px 0;
}

.empty-state-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.6;
	margin: 0 0 32px 0;
}

.empty-state-actions {
	margin-bottom: 32px;
}

.empty-state-btn {
	display: inline-flex;
	align-items: center;
	padding: 12px 24px;
	background-color: #3b82f6;
	color: #ffffff;
	text-decoration: none;
	border-radius: 8px;
	font-weight: 500;
	font-size: 14px;
	transition: all 0.2s ease;
}

.empty-state-btn:hover {
	background-color: #2563eb;
	color: #ffffff;
	text-decoration: none;
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.empty-state-help {
	background: #f9fafb;
	border-radius: 8px;
	padding: 20px;
	text-align: left;
}

.help-item {
	display: flex;
	align-items: center;
	font-size: 13px;
	color: #374151;
	margin-bottom: 8px;
}

.help-item:last-child {
	margin-bottom: 0;
}

.help-item i {
	color: #6b7280;
	font-size: 14px;
}

/* Campaign Type Tabs Styles */
.campaign-type-tab {
	border-right: 1px solid #e9ecef;
}

.campaign-type-tab:last-of-type {
	border-right: 2px solid #e9ecef;
	margin-right: 16px;
}

.tabs-divider {
	width: 1px;
	height: 24px;
	background-color: #dee2e6;
	margin: 0 8px;
	align-self: center;
}

/* Additional table column styles */
.sequences-col {
	flex: 1;
	min-width: 120px;
}

.sequences-info {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.sequences-count {
	font-weight: 600;
	color: #212529;
	font-size: 0.95rem;
}

.sequences-detail {
	font-size: 0.8rem;
	color: #6c757d;
}

.author-col {
	flex: 1;
	min-width: 120px;
}

.author-info {
	font-size: 14px;
	color: #374151;
	font-weight: 500;
}

/* Enhanced sequences column styles */
.sequences-detailed {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.sequences-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.sequence-item {
	background: #f8f9fa;
	border-radius: 4px;
	padding: 6px 8px;
	border-left: 3px solid #e9ecef;
}

.sequence-content {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
}

.sequence-position {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	min-width: 24px;
}

.sequence-type-badge {
	padding: 2px 6px;
	border-radius: 12px;
	font-size: 9px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.sequence-type-badge.email {
	background-color: #e0f2fe;
	color: #0369a1;
}

.sequence-type-badge.postcard {
	background-color: #f0f9ff;
	color: #0284c7;
}

.sequence-type-badge.letter {
	background-color: #dbeafe;
	color: #1e40af;
}


.sequence-status {
	padding: 2px 6px;
	border-radius: 8px;
	font-size: 10px;
	font-weight: 500;
	text-transform: capitalize;
	display: flex;
	align-items: center;
	gap: 4px;
}

.sequence-status i {
	font-size: 8px;
}

.status-pending {
	background-color: #f3f4f6;
	color: #374151;
}

.status-pending i {
	opacity: 0.6;
}

.status-active {
	background-color: #dcfce7;
	color: #166534;
}

.status-sending {
	background-color: #dbeafe;
	color: #1e40af;
	animation: pulse 2s infinite;
}

.status-completed {
	background-color: #dcfce7;
	color: #166534;
}

.status-paused {
	background-color: #fef3c7;
	color: #92400e;
}

@keyframes pulse {
	0% {
		box-shadow: 0 0 0 0 rgba(30, 64, 175, 0.4);
	}
	70% {
		box-shadow: 0 0 0 4px rgba(30, 64, 175, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(30, 64, 175, 0);
	}
}

.sequence-start-date {
	font-size: 10px;
	color: #6b7280;
	background: #ffffff;
	padding: 2px 4px;
	border-radius: 4px;
	border: 1px solid #e5e7eb;
}

.sequence-cost {
	font-size: 10px;
	font-weight: 600;
	color: #059669;
	background: #ecfdf5;
	padding: 2px 4px;
	border-radius: 4px;
}

.no-sequences {
	padding: 12px;
	text-align: center;
}

.empty-sequences {
	font-size: 12px;
	color: #9ca3af;
	font-style: italic;
}

.sequence-preview {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 8px;
	background-color: #f8f9ff;
}
</style>
